import { createApiClient, type ApiClient } from "@torah-auctions/api-client";
import { resolveApiBaseUrl } from "./api-url";

// Resolved at module-load. The override (set in Settings → Desktop)
// takes priority over VITE_API_URL — pointing every office desktop at
// one LAN-hosted API gives them real-time shared state without
// round-tripping through the cloud sync engine.
const baseUrl = resolveApiBaseUrl();

// Singleton — every hook + component imports this. Cookies are forwarded by
// default so Better Auth's session works seamlessly.
export const api: ApiClient = createApiClient(baseUrl);

export const API_BASE_URL = baseUrl;

// Convenience: throw on non-2xx so React Query catches errors. Hono's typed
// responses don't throw by default; we wrap to keep call sites tidy.
//
// Zod-validator failures come back as { success: false, error: { issues: [...] } }
// — the raw object stringifies to "[object Object]" in toasts. We flatten
// the issue list into a readable message so users see what's wrong.
export async function unwrap<T>(
  response: { ok: boolean; status: number; json: () => Promise<unknown> },
): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(extractErrorMessage(body, response.status), response.status);
  }
  return (await response.json()) as T;
}

function extractErrorMessage(body: unknown, status: number): string {
  if (!body || typeof body !== "object") return `HTTP ${status}`;
  const o = body as Record<string, unknown>;

  // Our HTTPException handler in apps/api: { error: "Slug is already taken" }
  if (typeof o.error === "string") return o.error;

  // zod-validator default shape: { success: false, error: { issues: [...] } }
  // Each issue has { path: string[], message: string }.
  if (
    typeof o.error === "object" &&
    o.error !== null &&
    "issues" in (o.error as object) &&
    Array.isArray((o.error as { issues: unknown }).issues)
  ) {
    const issues = (o.error as { issues: { path: unknown[]; message: string }[] })
      .issues;
    return issues
      .slice(0, 3)
      .map((i) => {
        const field = i.path?.filter((p) => p !== "json").join(".") || "request";
        return `${field}: ${i.message}`;
      })
      .join("; ");
  }

  // Last resort — surface anything stringy on the body, or the status code.
  if (typeof o.message === "string") return o.message;
  return `HTTP ${status}`;
}

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
    this.name = "ApiError";
  }
}
